import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import {
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel
} from '@material-ui/core';

import AppSnackbar from '../../common/AppSnackbar';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import PageTempalteHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import SearchBox from '../../common/SearchBox';
import { useFindAgreements } from './Agreement.hooks';
import PublishStatusLabels from '../../common/PublishStatusLabels';
import AgreementCreateDialog from './dialogs/AgreementCreateDialog';

const AgreementAdminListPage: React.FC = () => {
  const history = useHistory();
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 0));
  const [limit, setLimit] = useQueryParam(
    'limit',
    withDefault(NumberParam, 100)
  );
  const [order, setOrder] = useQueryParam('order', StringParam);
  const [search, setSearch] = useQueryParam('search', StringParam);
  const [orderDirection, setOrderDirection] = useQueryParam(
    'direction',
    StringParam
  );
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const { list, isLoading, error, get } = useFindAgreements();
  useEffect(() => {
    const options = {} as any;
    const params = {
      page: Number(page),
      limit: Number(limit)
      // order: String(order || ''),
    } as any;
    if (order) {
      options.order = { [order]: orderDirection || 'ASC' };
    }
    if (search) {
      params.search = search;
    }
    params.options = JSON.stringify(options);
    get(params);
  }, [get, page, limit, order, orderDirection, search]);

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLimit(+event.target.value);
    setPage(0);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <PageTemplate>
      <PageTempalteHeader>
        <h2>Agreements</h2>
        <SearchBox value={search} onSearch={handleSearch} />
        <PageTemplateHeaderActions>
          <Button
            variant='contained'
            color='secondary'
            onClick={() => setIsOpenDialog(true)}
          >
            Add New Agreement
          </Button>
          <AgreementCreateDialog
            open={isOpenDialog}
            onCancel={() => setIsOpenDialog(false)}
            onCreated={agreement =>
              history.push(`./agreements/${agreement.id}`)
            }
          />
        </PageTemplateHeaderActions>
      </PageTempalteHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={order === 'name'}
                    direction={getSortDirection('name')}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell>
                  <TableSortLabel
                    active={order === 'version'}
                    direction={getSortDirection('version')}
                    onClick={() => handleSort('version')}
                  >
                    Version
                  </TableSortLabel>
                </TableCell> */}
                <TableCell>
                  <TableSortLabel
                    active={order === 'status'}
                    direction={getSortDirection('status')}
                    onClick={() => handleSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'modified'}
                    direction={getSortDirection('modified')}
                    onClick={() => handleSort('modified')}
                  >
                    Modified
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'modifiedBy'}
                    direction={getSortDirection('modifiedBy')}
                    onClick={() => handleSort('modifiedBy')}
                  >
                    Modified By
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {list &&
                list.data.map(agreement => (
                  <TableRow
                    hover
                    key={agreement.id}
                    onClick={() => history.push(`./agreements/${agreement.id}`)}
                  >
                    <TableCell>{agreement.name}</TableCell>
                    {/* <TableCell>{agreement.version}</TableCell> */}
                    <TableCell>
                      <PublishStatusLabels
                        status={agreement.status}
                        version={agreement.version}
                      />
                    </TableCell>
                    <TableCell>
                      {agreement.modified &&
                        format(agreement.modified, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {agreement.modifiedBy?.firstName}{' '}
                      {agreement.modifiedBy?.lastName}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {list && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={list.total}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowPerPage}
          />
        )}
        <AppSnackbar severity='error' open={!!error}>
          There was an error getting the list from the server.
        </AppSnackbar>
      </PageTemplateCardOver>
    </PageTemplate>
  );
};

export default AgreementAdminListPage;
